/**
 * The rate card as this client sees it — `modules/catalog/routes.ts`.
 *
 * The tier and the currency come from the client's **row**, never from the body
 * (`CLAUDE.md` §The catalog), so neither hook takes them.
 *
 * `useComputePrice` is a mutation rather than a query because the route is a `POST`
 * — the request is a structure, not a path — and because `client/billing.html` §3
 * asks for it on every tick of a checkbox. It commits nothing and carries no
 * `Idempotency-Key`.
 */

import type { PriceRequest } from '@fareast/shared'

import { useApiMutation, useApiQuery } from '../hooks.js'
import { queryKeys } from '../keys.js'

/**
 * `GET /api/catalog` — the services, extras, add-ons and presets, in order.
 *
 * `inline` is the request composer's: it is a window over every page of the portal, and
 * a rate card that did not load is something to say inside the window rather than a
 * reason to take down the page behind it.
 */
export function useCatalog(options: { readonly inline?: boolean } = {}) {
  return useApiQuery({
    key: queryKeys.catalog.all(),
    request: (api) => api.rest.catalog.$get(),
    ...(options.inline === undefined ? {} : { inline: options.inline }),
    /* The card changes when somebody adds a price, which is not on this page's clock. */
    staleTime: 5 * 60 * 1000,
  })
}

/** `POST /api/catalog/compute` — the indicative delta under the composer. */
export function useComputePrice() {
  return useApiMutation({
    module: 'catalog',
    request: (api, request: PriceRequest) => api.rest.catalog.compute.$post({ json: { request } }),
  })
}
